
import auth from '@/utils/firebase';
import { 
  createUserWithEmailAndPassword, 
  sendEmailVerification,
  applyActionCode,
  User
} from 'firebase/auth';
import { AuthResult, actionCodeSettings } from './types';
import { storeUserData, updateUserProfile } from './userManagement';

// Check if the current URL is a valid account setup link
export const isAccountSetupLink = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.get('mode') === 'verifyEmail' && !!params.get('oobCode');
};

// Verify the email using Firebase's applyActionCode
export const verifyEmail = async (): Promise<AuthResult> => {
  try {
    const params = new URLSearchParams(window.location.search);
    const oobCode = params.get('oobCode');
    
    if (!oobCode) {
      return { success: false, error: 'Invalid verification link' };
    }
    
    await applyActionCode(auth, oobCode);
    return { success: true };
  } catch (error: unknown) {
    console.error('Email verification error:', error);
    const errorMessage = error instanceof Error ? error.message : 'There was an error verifying your email';
    return { 
      success: false, 
      error: errorMessage
    };
  }
};

// Complete account setup with email and password
export const completeAccountSetup = async (
  email: string, 
  password: string, 
  displayName: string
): Promise<AuthResult> => {
  try {
    // Create user with email and password
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update display name
    await updateUserProfile(user, displayName);
    
    // Send email verification if not already verified
    if (!user.emailVerified) {
      await sendEmailVerification(user, actionCodeSettings);
    }
    
    // Store additional user data in Firestore
    await storeUserData(user.uid, {
      email,
      displayName,
      createdAt: new Date().toISOString(),
    });
    
    return { success: true, user };
  } catch (error: unknown) {
    console.error('Account setup error:', error);
    const errorMessage = error instanceof Error ? error.message : 'There was an error setting up your account';
    return { 
      success: false, 
      error: errorMessage
    };
  }
};
